import React, { useState, useEffect, useRef } from "react";
import { useTemplateStore } from "@/zustand/templateStore";
import { useProductData } from "@/zustand/productStore";
import { useCartData } from "@/zustand/cartStore";
import { useUpsellData } from "@children/zustand/upsellStore";
import axios from "axios";
import FormAccordionsDivided from "@children/components/forms/formAccordionsDivided";

const Form = () => {
  const { isLoading, setLoadingState, isMobile } = useTemplateStore();
  const { paypalHandler, warrantyState, selectedProduct, discountCode, variants, isDiscountApplied } = useCartData();
  const { upsellProduct } = useUpsellData();
  const productData = useProductData();

  const formLabels = window.post_template?.checkout_v4_form.checkout_v4_formLabels;
  const formSections = window.post_template?.checkout_v4_form.checkout_v4_formHeadings;

  const [backError, setBackError] = useState();
  const backErrorField = useRef(null);

  const scrollIntoView = (element) => {
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }
  };

  const cleanCheck = (isMobile == false && window.cleanDesktop) || (isMobile == true && window.cleanMobile);

  const directCheck = window.msg?.clean_checkout_if_direct_customer && window.directCheck;
  const enableCleanVersion = directCheck || cleanCheck;

  useEffect(() => {
    if (backError) {
      scrollIntoView(backErrorField?.current);
    }
  }, [backError]);

  const handlePaypalSubmit = async () => {
    const variantsEnabled = productData?.enable_color_variants || productData?.enable_size_variants;
    const shippingProtectionLabel =
      window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_shippingProtectionLabel;
    const extendedWarrantyLabel = window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_warrantyLabel;
    const warrantyType = productData?.select_warranty_type_cc;
    const warrantyTitle =
      warrantyType?.label?.toLowerCase() === "extended warranty" ? extendedWarrantyLabel : shippingProtectionLabel;

    const paypalData = {
      products: {
        currencyCode: productData?.currency_code,
        paypalMerchant: productData?.paypal_merchant,
        warrantyTitle: warrantyTitle,
        campaignId: productData?.konnektive_campaign_id?.trim(),
        product: selectedProduct,
        nextStep: productData?.next_page_on_success,
        multiplyPriceByAmount: productData?.multiply_price_by_amount,
        warranty_price: productData?.add_warranty_for_product === false ? 0 : selectedProduct?.warranty_price,
        warrantyPriceCalculation: productData?.add_warranty_for_product == false,
        isWarrantySelected: productData?.add_warranty_for_product ?? false,
        warrantyState: warrantyState,
        warrantyType: productData?.select_warranty_type_cc ?? null,
        postId: window.postId,
        variants: variantsEnabled && variants.length > 0 ? variants?.slice(0, selectedProduct?.amount) : null,
      },
      upsell_product: upsellProduct
        ? {
            ...upsellProduct,
            old_price: Number(upsellProduct?.product_list_unit_old_price) * Number(selectedProduct?.amount),
            amount: selectedProduct?.amount,
          }
        : null,
      nextStep: productData?.next_page_on_success,
      upsell: productData?.paypal_upsell ? productData?.paypal_upsell_settings : null,
      thankYou: productData?.paypal_thank_you_page,
      return_to: window.location.href,
      affiliate: window.affiliate,
      discountsEnabled: productData?.enable_discount_codes,
      appliedDiscountCode: discountCode,
      isDiscountApplied: isDiscountApplied,
      productsArray: productData?.new_product_array_logic,
      warrantyAmount: upsellProduct ? selectedProduct?.amount * 2 : selectedProduct?.amount,
    };

    setLoadingState(true);

    try {
      const response = await axios.post("/wp-json/checkout/v1/order/paypal", paypalData);
      const data = response.data;
      if (data.result === "ERROR") {
        setBackError(data.message);
      } else {
        window.location.href = data;
      }
    } catch (error) {
      setBackError(error?.message);
    } finally {
      setLoadingState(false);
    }
  };

  const handleCreditCardSubmit = async (formData) => {
    const navigator = window.navigator;
    const variantsEnabled = productData?.enable_color_variants || productData?.enable_size_variants;

    const shippingProtectionLabel =
      window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_shippingProtectionLabel;
    const extendedWarrantyLabel = window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_warrantyLabel;
    const warrantyType = productData?.select_warranty_type_cc;
    const warrantyTitle =
      warrantyType?.label?.toLowerCase() === "extended warranty" ? extendedWarrantyLabel : shippingProtectionLabel;

    const browserData = {
      acceptHeader: "application/json",
      colorDepth: screen.colorDepth,
      javaEnabled: navigator.javaEnabled(),
      javaScriptEnabled: true,
      language: navigator.language,
      screenHeight: screen.height,
      screenWidth: screen.width,
      timeZoneOffset: new Date().getTimezoneOffset().toString(),
      userAgent: navigator.userAgent,
    };

    const requestData = {
      data: formData,
      affiliate: window.affiliate,
      products: {
        currencyCode: productData?.currency_code,
        campaignId: productData?.konnektive_campaign_id?.trim(),
        isWarrantySelected: productData?.add_warranty_for_product,
        multiplyPriceByAmount: productData?.multiply_price_by_amount,
        nextStep: productData?.next_page_on_success,
        product: selectedProduct,
        warranty_price: productData?.add_warranty_for_product === false ? 0 : selectedProduct?.warranty_price,
        warrantyType: productData?.select_warranty_type_cc ?? null,
        warrantyTitle: warrantyTitle,
        warrantyPriceCalculation: productData?.add_warranty_for_product == false,
        postId: window.postId,
        warrantyState: warrantyState,
        variants: variantsEnabled && variants?.length > 0 ? variants?.slice(0, selectedProduct?.amount) : null,
      },
      upsell_product: upsellProduct
        ? {
            ...upsellProduct,
            old_price: Number(upsellProduct?.product_list_unit_old_price) * Number(selectedProduct?.amount),
            amount: selectedProduct?.amount,
          }
        : null,
      upsell_path: productData?.next_page_on_success,
      return_to: window.location.href,
      browserData: browserData,
      paypalMerchant: productData?.paypal_merchant,
      discountsEnabled: productData?.enable_discount_codes,
      appliedDiscountCode: discountCode,
      bundles: productData?.show_bundle_design,
      isDiscountApplied: isDiscountApplied,
      warrantyAmount: upsellProduct ? selectedProduct?.amount * 2 : selectedProduct?.amount,
    };

    setLoadingState(true);

    try {
      const response = await axios.post("/wp-json/checkout/v1/order", requestData);
      const data = response.data;
      if (data.result === "ERROR") {
        setBackError(data.message);
      } else if (data.result === "SUCCESS") {
        window.location.href = productData?.next_page_on_success + "?orderId=" + data.message.orderId;
      } else if (data.result === "MERC_REDIRECT") {
        const message = data.message;
        if (message && message.url) {
          window.location.href = message.url;
        } else if (message && message.script) {
          eval(message.script);
        }
      }
    } catch (error) {
      setBackError(error?.message);
    } finally {
      setLoadingState(false);
    }
  };

  return (
    <>
      <FormAccordionsDivided
        labels={formLabels}
        headings={formSections}
        formSubmited={isLoading}
        handleCreditCardSubmit={handleCreditCardSubmit}
        handlePaypalSubmit={handlePaypalSubmit}
        compliance={false}
        cleanVersion={!!enableCleanVersion}
        paypalHandler={paypalHandler}
      />
      {backError && (
        <div ref={backErrorField} className="error-back">
          {backError}
        </div>
      )}
    </>
  );
};

export { Form };
